/**
 * Website inbox: shared brain settings
 * ====================================
 * 2026-09-28 (Docs/PROJECT_SHARED_PIPELINE_AND_FORMS.md, Phase 2 tasks 2.5,
 * 2.12). Turns the shared pipeline on for one website inbox (AI answers,
 * Digital Companion, forms, automation -- the same as WhatsApp) and sets the
 * greeting with its language / quick-reply buttons (shown as tap-to-reply
 * chips). Saved on the inbox: channels.settings.shared_brain / bot_name /
 * greeting_buttons and the greeting_enabled / greeting_message columns --
 * the same settings the WhatsApp inbox uses.
 *
 * 2026-09-28 (Phase 5): "Let visitors call" -- channels.settings.web_calling.
 * A call button in the widget; the call is answered like a WhatsApp call
 * (Digital Companion / AI first, a human agent when it escalates).
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add, Delete, Save } from '@mui/icons-material';
import api from '../services/api';

interface GreetingButton {
  id: string;
  title: string;
  sets_language: string; // '' = not a language picker; else an ISO code (en, fr, sw...)
}

interface Props {
  inboxId: number;
}

// The widget shows up to this many chips under one message.
const MAX_BUTTONS = 10;

const WebsiteSharedBrainSettings: React.FC<Props> = ({ inboxId }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [botName, setBotName] = useState('');
  const [greetingEnabled, setGreetingEnabled] = useState(false);
  const [greetingMessage, setGreetingMessage] = useState('');
  const [buttons, setButtons] = useState<GreetingButton[]>([]);
  const [webCalling, setWebCalling] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get(`/inboxes/${inboxId}`);
        const inbox = res.data || {};
        const settings = inbox.settings || {};
        setEnabled(!!settings.shared_brain);
        setBotName(settings.bot_name || '');
        setWebCalling(!!settings.web_calling);
        setGreetingEnabled(!!inbox.greeting_enabled);
        setGreetingMessage(inbox.greeting_message || '');
        setButtons(
          (settings.greeting_buttons || []).map((b: any) => ({
            id: String(b.id || ''),
            title: String(b.title || ''),
            sets_language: String(b.sets_language || ''),
          })),
        );
      } catch (err: any) {
        setMessage({ type: 'error', text: err?.response?.data?.detail || 'Could not load the inbox' });
      } finally {
        setLoading(false);
      }
    })();
  }, [inboxId]);

  const updateButton = (index: number, field: keyof GreetingButton, value: string) =>
    setButtons((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)));

  const save = async () => {
    if (greetingEnabled && !greetingMessage.trim()) {
      setMessage({ type: 'error', text: 'Write the greeting message, or turn the greeting off.' });
      return;
    }
    const cleaned = buttons
      .map((b) => ({ ...b, title: b.title.trim(), sets_language: b.sets_language.trim().toLowerCase() }))
      .filter((b) => b.title);
    setSaving(true);
    setMessage(null);
    try {
      await api.put(`/inboxes/${inboxId}`, {
        greeting_enabled: greetingEnabled,
        greeting_message: greetingMessage,
        settings: {
          shared_brain: enabled,
          bot_name: botName.trim() || null,
          greeting_buttons: cleaned,
          web_calling: webCalling,
        },
      });
      setButtons(cleaned);
      setMessage({ type: 'success', text: 'Saved' });
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Could not save' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
        label="Answer website visitors with the shared brain"
      />
      <Typography variant="body2" color="text.secondary">
        When on, messages from this website inbox get the same automated handling as WhatsApp: AI answers,
        Digital Companion questions and tickets, forms (asked in chat or as a form card), automation rules,
        language buttons and photo checks. When off, messages are only saved for your agents, as before.
      </Typography>
      <TextField
        label="Name shown on automated replies"
        placeholder="Assistant"
        value={botName}
        onChange={(e) => setBotName(e.target.value)}
        size="small"
      />

      <Divider />

      <FormControlLabel
        control={<Switch checked={greetingEnabled} onChange={(e) => setGreetingEnabled(e.target.checked)} />}
        label="Greet new conversations"
      />
      {greetingEnabled && (
        <TextField
          label="Greeting message"
          value={greetingMessage}
          onChange={(e) => setGreetingMessage(e.target.value)}
          multiline
          minRows={2}
          size="small"
        />
      )}

      <Box>
        <Typography variant="subtitle2">Greeting buttons (tap-to-reply chips)</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          A button with a language code (e.g. en, fr, sw) sets the visitor's language, like the WhatsApp
          language buttons. The conversation-start forms then start after the tap.
        </Typography>
        {buttons.map((b, i) => (
          <Stack key={b.id || i} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField
              label="Button text"
              value={b.title}
              onChange={(e) => updateButton(i, 'title', e.target.value)}
              size="small"
              sx={{ flex: 2 }}
            />
            <TextField
              label="Sets language (optional)"
              placeholder="en"
              value={b.sets_language}
              onChange={(e) => updateButton(i, 'sets_language', e.target.value)}
              size="small"
              sx={{ flex: 1 }}
            />
            <IconButton aria-label="Remove button" onClick={() => setButtons((prev) => prev.filter((_, j) => j !== i))}>
              <Delete fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Button
          size="small"
          startIcon={<Add />}
          disabled={buttons.length >= MAX_BUTTONS}
          onClick={() => setButtons((prev) => [...prev, { id: `btn_${Date.now()}`, title: '', sets_language: '' }])}
        >
          Add button
        </Button>
      </Box>

      <Divider />

      <FormControlLabel
        control={<Switch checked={webCalling} onChange={(e) => setWebCalling(e.target.checked)} />}
        label="Let visitors call"
      />
      <Typography variant="body2" color="text.secondary">
        Adds a call button to the chat widget. The call is answered like a WhatsApp call: Digital Companion
        and the AI first, and an agent joins when Digital Companion escalates (a call-agent automation rule;
        the agent answers in the dashboard or the agent app). Calls use speech and AI minutes.
      </Typography>

      {message && <Alert severity={message.type}>{message.text}</Alert>}
      <Box>
        <Button variant="contained" startIcon={<Save />} onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </Box>
    </Stack>
  );
};

export default WebsiteSharedBrainSettings;
